import { isSelectionAncestorOf } from "./ancestors";
import { selectionEquals } from "./equals";
import type { DocumentSection, Selection } from "../types";

export function isSameBlockTarget(
  selection: Selection,
  rowId: string,
  columnId: string,
  blockId: string,
): boolean {
  if (selection.kind === 'block') {
    return (
      selection.rowId === rowId &&
      selection.columnId === columnId &&
      selection.blockId === blockId
    );
  }
  if (selection.kind === 'buttonRowSlot') {
    return (
      selection.rowId === rowId &&
      selection.columnId === columnId &&
      selection.blockId === blockId
    );
  }
  return false;
}

export function isDirectBlockSelection(
  selection: Selection,
  rowId: string,
  columnId: string,
  blockId: string,
): boolean {
  return (
    selection.kind === 'block' &&
    selection.rowId === rowId &&
    selection.columnId === columnId &&
    selection.blockId === blockId
  );
}

export function isDirectBlockDragTarget(
  selection: Selection,
  rowId: string,
  columnId: string,
  blockId: string,
): boolean {
  if (isDirectBlockSelection(selection, rowId, columnId, blockId)) {
    return true;
  }
  return (
    selection.kind === 'buttonRowSlot' &&
    selection.rowId === rowId &&
    selection.columnId === columnId &&
    selection.blockId === blockId
  );
}

export function isContainerSelectionOverTarget(
  selection: Selection,
  target: Selection,
  sections: DocumentSection[],
): boolean {
  if (selection.kind === 'email' || selectionEquals(selection, target)) {
    return false;
  }
  return isSelectionAncestorOf(selection, target, sections);
}

export function shouldDeferChildPointerActivation(
  selection: Selection,
  target: Selection,
  sections: DocumentSection[],
  event: Pick<MouseEvent, 'metaKey' | 'ctrlKey' | 'shiftKey' | 'type'>,
): boolean {
  if (event.type !== 'pointerdown' && event.type !== 'mousedown') {
    return false;
  }
  if (event.metaKey || event.ctrlKey || event.shiftKey) {
    return false;
  }
  return isContainerSelectionOverTarget(selection, target, sections);
}

export function buttonRowSlotIndexForBlock(
  target: Selection | null,
  rowId: string,
  columnId: string,
  blockId: string,
): number | null {
  if (!target || target.kind !== 'buttonRowSlot') {
    return null;
  }
  if (
    target.rowId !== rowId ||
    target.columnId !== columnId ||
    target.blockId !== blockId
  ) {
    return null;
  }
  return target.slotIndex;
}

export function isDirectButtonRowBlockTarget(
  target: Selection | null,
  rowId: string,
  columnId: string,
  blockId: string,
): boolean {
  return (
    target?.kind === 'block' &&
    target.rowId === rowId &&
    target.columnId === columnId &&
    target.blockId === blockId
  );
}

export function resolveButtonRowSlotIndex(
  hover: Selection | null,
  selection: Selection,
  rowId: string,
  columnId: string,
  blockId: string,
): number | null {
  const hoverSlot = buttonRowSlotIndexForBlock(hover, rowId, columnId, blockId);
  if (hoverSlot !== null) {
    return hoverSlot;
  }
  const selectionSlot = buttonRowSlotIndexForBlock(
    selection,
    rowId,
    columnId,
    blockId,
  );
  if (selectionSlot !== null) {
    return selectionSlot;
  }
  if (isDirectButtonRowBlockTarget(hover, rowId, columnId, blockId)) {
    return null;
  }
  return null;
}
